import { useEffect, useRef, useState } from "react";
import {
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

interface PriceNumberInputProps {
  value: number;
  onChange: (valueAsString: string, valueAsNumber: number) => void;
  onBlur?: () => void;
  min?: number;
  step?: number;
  precision?: number;
  placeholder?: string;
}

const stepperProps = {
  border: "none",
  bg: "primary.50",
  color: "primary.600",
  transition: "all 0.15s ease",
  _hover: { bg: "primary.100", color: "primary.700" },
  _active: { bg: "primary.200" },
  _disabled: { opacity: 0.4, cursor: "not-allowed" },
};

function formatDisplayValue(value: number, precision: number): string {
  if (Number.isNaN(value)) {
    return "";
  }
  if (precision === 0) {
    return String(Math.round(value));
  }
  const fixed = value.toFixed(precision);
  return fixed.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "") || "0";
}

function isValidInput(value: string, allowDecimals: boolean): boolean {
  if (value === "") {
    return true;
  }
  return allowDecimals ? /^\d*\.?\d*$/.test(value) : /^\d+$/.test(value);
}

export function normalizeNumericValue(
  value: number | string | null | undefined,
  isInteger = false,
): number {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return isInteger ? Math.max(0, Math.floor(parsed)) : Math.max(0, parsed);
}

export default function PriceNumberInput({
  value,
  onChange,
  onBlur,
  min = 0,
  step = 0.01,
  precision = 2,
  placeholder = "0",
}: PriceNumberInputProps) {
  const allowDecimals = precision > 0;
  const isFocusedRef = useRef(false);
  const [displayValue, setDisplayValue] = useState(() =>
    formatDisplayValue(value, precision),
  );

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDisplayValue(formatDisplayValue(value, precision));
    }
  }, [value, precision]);

  const emitChange = (valueAsString: string) => {
    if (valueAsString === "" || valueAsString === ".") {
      onChange(valueAsString, 0);
      return;
    }

    if (allowDecimals && valueAsString.endsWith(".")) {
      const partial = parseFloat(valueAsString);
      onChange(valueAsString, Number.isNaN(partial) ? 0 : partial);
      return;
    }

    const parsed = allowDecimals
      ? parseFloat(valueAsString)
      : parseInt(valueAsString, 10);
    if (!Number.isNaN(parsed)) {
      onChange(valueAsString, parsed);
    }
  };

  const handleChange = (valueAsString: string) => {
    if (!isValidInput(valueAsString, allowDecimals)) {
      return;
    }
    setDisplayValue(valueAsString);
    emitChange(valueAsString);
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    const normalized = normalizeNumericValue(displayValue, !allowDecimals);
    const formatted = formatDisplayValue(normalized, precision);
    setDisplayValue(formatted);
    onChange(formatted, normalized);
    onBlur?.();
  };

  return (
    <NumberInput
      min={min}
      step={step}
      precision={precision}
      value={displayValue}
      onChange={handleChange}
      parse={(val) => val}
      format={(val) => val}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onBlur={handleBlur}
      clampValueOnBlur
      keepWithinRange
      size="md"
    >
      <NumberInputField
        placeholder={placeholder}
        borderColor="gray.200"
        borderRightRadius={0}
        bg="white"
        fontWeight="medium"
        inputMode={allowDecimals ? "decimal" : "numeric"}
        _hover={{ borderColor: "gray.300" }}
        _focus={{
          borderColor: "primary.400",
          boxShadow: "0 0 0 1px var(--chakra-colors-primary-400)",
        }}
      />
      <NumberInputStepper
        width="2.25rem"
        borderLeft="1px solid"
        borderColor="gray.200"
        overflow="hidden"
        borderTopRightRadius="md"
        borderBottomRightRadius="md"
      >
        <NumberIncrementStepper {...stepperProps} height="50%">
          <ChevronUpIcon boxSize={3} />
        </NumberIncrementStepper>
        <NumberDecrementStepper {...stepperProps} height="50%">
          <ChevronDownIcon boxSize={3} />
        </NumberDecrementStepper>
      </NumberInputStepper>
    </NumberInput>
  );
}
